/**
 * Transactions API client. Wraps fetch + the Bearer-token apiFetch from auth/.
 */
import { apiFetch, ApiError } from '../auth/api';

export type TxType = 'auction' | 'listing' | 'buyer';
export type TxStatus = 'active' | 'under_contract' | 'closed' | 'cancelled';
/** Dead-deal outcome (migration 0143). Set alongside status='cancelled'. */
export type ListingOutcome = 'cancelled' | 'withdrawn' | 'expired';
/** Status filter values for the list — real statuses plus the two dead
 *  outcomes that also live under status='cancelled'. */
export type TxStatusFilter = TxStatus | 'withdrawn' | 'expired';
/** Migration 0146 (Daniel 2026-07-27). NULL/absent = not specified. */
export type BuyerType = 'traditional' | 'investor';

/** Human label for the buyer-type badge. NULL renders as "Not set" — it is
 *  never treated as a traditional buyer. */
export function buyerTypeLabel(t: BuyerType | null | undefined): string {
  if (t === 'investor') return 'Investor';
  if (t === 'traditional') return 'Traditional buyer';
  return 'Not set';
}

/** Badge colors. Investor uses the team's existing investor green (#065F46
 *  on a soft green fill) so it pops next to the neutral chips. */
export function buyerTypeColor(t: BuyerType | null | undefined): { bg: string; fg: string } {
  if (t === 'investor') return { bg: '#D1FAE5', fg: '#065F46' };
  if (t === 'traditional') return { bg: 'var(--bg-surface-2)', fg: 'var(--text-heading)' };
  return { bg: 'var(--bg-surface-2)', fg: 'var(--text-muted)' };
}

export interface Transaction {
  /** Build 30. Null means nobody has decided yet, which is a real state and
   *  not a missing value to be defaulted away. */
  lead_origin?: LeadOrigin | null;
  /** Build 30. 1 when this is a flip: flat fee instead of a split, and its
   *  volume stays out of the tier ladder. */
  is_flip?: number;
  id: number;
  team_id: number;
  type: TxType;
  status: TxStatus;
  current_phase_slug: string;
  primary_agent_id: number | null;
  primary_contact_id: number | null;
  address_street: string | null;
  address_city: string | null;
  address_state: string | null;
  address_zip: string | null;
  list_price: number | null;       // cents
  sale_price: number | null;       // cents
  contract_date: number | null;    // unix seconds
  close_date: number | null;       // unix seconds
  // L-120 (Daniel 2026-07-22): first-class listing dates. list_date = date
  // the listing was taken; live_date = went live on market. Anchors for
  // relative task due dates + the completeness meter.
  list_date: number | null;        // unix seconds
  live_date: number | null;        // unix seconds
  // L-89 (Daniel 2026-07-08): closing time-of-day as freeform TEXT
  // ("9:00 AM"). Added in migration 0124 alongside the
  // {{close_time}} and {{close_date_time_formatted}} merge fields.
  close_time: string | null;
  /** L-123 (Daniel 2026-07-22): full closing / title location address. */
  close_location: string | null;
  // Dead-deal states (migration 0143). When listing_outcome is set the deal
  // is terminal ("dead"); status is 'cancelled'. Null = a live deal.
  listing_outcome: ListingOutcome | null;
  listing_outcome_at: number | null;   // unix seconds — when marked dead
  listing_outcome_note: string | null; // free-text detail, not countable
  /** Migration 0186: why it died, from the shared DEAD_REASONS list. Render it
   *  with deadReasonLabel, never as the raw key. Null when not recorded. */
  dead_reason: string | null;
  /** Migration 0186: 1 when the deal was under contract at the moment it died,
   *  0 when it was not, NULL for deals closed out before the column existed.
   *  NULL means unknown and must never be read as 0. */
  died_under_contract: number | null;
  /** Listing-agreement expiration date (unix seconds). Drives the daily
   *  10-day-out expiration reminder. Editable in the Key dates form. */
  listing_expiration_date: number | null;
  mls_number: string | null;
  kw_command_id: string | null;
  transactiondesk_id: string | null;
  notes: string;
  marketing_plan_json: string;
  /** JSON-encoded commission breakdown for the closing statement.
   *  See apps/web/src/lib/commission.ts for the parsed shape. */
  commission_breakdown_json: string;
  // Phase-12 additions — populated for contract/auction transactions
  earnest_money: number | null;       // cents
  reserve_price: number | null;       // cents (auction only)
  auction_date: number | null;        // unix seconds
  auction_time: string | null;        // "11:00 AM"
  auction_location: string | null;
  /** bidwichita.com URL for the auction page — added in migration 0029. */
  auction_link_url: string | null;
  /** YouTube / Vimeo URL — embedded on the public listing page. */
  video_url: string | null;
  // Lockbox + sign info (Daniel 2026-06-08, migration 0080). Listing +
  // auction transactions only — buyer transactions ignore these fields.
  lockbox_type: string | null;
  lockbox_code: string | null;
  lockbox_location: string | null;
  lockbox_notes: string | null;
  /** turnkey_hanging | office_sign | turnkey_auction_square | other */
  sign_type: string | null;
  /** Per-transaction deep link to the KW Command opportunity page. */
  kw_command_url: string | null;
  /** Build 14 (Daniel 2026-09-15): set when this deal is a lot of an auction. */
  parent_transaction_id: number | null;
  /** Build 14: 1 when this auction has lots under it. */
  is_lot_parent: number;
  /** Per-transaction deep link to the Authentisign envelope for this deal. */
  authentisign_url: string | null;
  financing_type: string | null;      // 'conventional'|'fha'|'va'|'cash'|'1031'
  /** Migration 0146 (Daniel 2026-07-27): traditional buyer vs investor.
   *  Buyer deals only. NULL = not specified — render as "Not set", never
   *  as "Traditional buyer". */
  buyer_type: BuyerType | null;
  /** Migration 0146: the team member who acted as showing assistant on a
   *  buyer deal. When set, 10% of the agent's commission is redirected to
   *  the team owner — see lib/commission.ts. Null = no assistant. */
  showing_assistant_user_id: number | null;
  /** Migration 0147: the producing agent's share of the in-house
   *  commission, 0..1 (0.6 = a 60/40 deal). NULL means the deal has no
   *  explicit split and the commission math falls back to the team default
   *  — see DEFAULT_AGENT_SPLIT_PCT in lib/commission.ts. */
  agent_split_pct: number | null;
  // L-47 (Daniel 2026-06-27): category of the property at this auction,
  // used by the L-48 recommender to match prior bidders. One of
  // 'residential' | 'commercial' | 'land' | 'multifamily' | 'other'.
  // Nullable — historic auctions can be backfilled lazily.
  property_type: string | null;
  // Milestone deadlines (migration 0020) — surfaced on the client dates strip
  // for both buyers and sellers. All optional; agents fill them in once known.
  // em_deadline added in 0115 (Phase 2 deadline scan).
  em_deadline: number | null;
  inspection_deadline: number | null;
  appraisal_deadline: number | null;
  financing_deadline: number | null;
  walkthrough_date: number | null;
  // L-78 (Daniel 2026-07-04): photo shoot scheduling fields for listings.
  // photo_appointment_at is a unix seconds datetime (date + time).
  // photos_url is the delivered gallery link (Dropbox/Google/Aryeo).
  photo_appointment_at: number | null;
  photographer_name: string | null;
  photos_url: string | null;
  // L-81 (Daniel 2026-07-04): inspection + appraisal scheduling.
  // *_scheduled_at is the actual appointment date+time (unix seconds);
  // *_deadline (already declared above) is the contract last-day.
  inspection_scheduled_at: number | null;
  inspector_name: string | null;
  inspector_company: string | null;
  appraisal_scheduled_at: number | null;
  appraiser_name: string | null;
  appraiser_company: string | null;
  share_token: string | null;         // null when not published
  share_token_created_at: number | null;
  /** Human-readable URL slug for /public/l/:slug. Null until the agent
   *  picks one on the Marketing tab. Distinct from share_token (which
   *  remains the random-token fallback URL). */
  public_slug: string | null;
  /** MLS listing description, drafted by Claude and editable in the
   *  Marketing tab. Null until generated. (Migration 0052.) */
  mls_caption: string | null;
  mls_caption_updated_at: number | null;
  mls_caption_updated_by_user_id: number | null;
  /** Round L-8 (2026-06-09): unix seconds the DA was last applied. Null
   *  until the agent uploads + applies a Disbursement Authorization. */
  da_verified_at: number | null;
  /** Round L-14 (Daniel 2026-06-09): joined buyer/seller name from
   *  transaction_parties. Drives Board card label + Detail h1 when
   *  there's no street address. Null when no parties on the deal. */
  primary_party_name: string | null;
  /** Round L-7: pre-approval/budget fields (already on leads — mirrored
   *  here in case the converted transaction needs them later). */
  pay_to_office_cents: number | null;
  // L-57 (Daniel 2026-06-29): per-listing voice + audience selection
  // for AI content generators. Remembered so the agent doesn't pick
  // every time. Null = default behavior (agent voice + general buyer).
  voice_profile_kind: 'agent' | 'team' | null;
  /** JSON: { general: bool, client_avatar_ids: number[], buyer_audience_ids: number[] }.
   *  Empty/null = general buyer only (the pre-L-57 default). */
  target_audiences_json: string | null;
  /** First listing photo (by position) — hero image on the client/lender
   *  portals. Null when no photos uploaded. Server-joined on GET /:id. */
  hero_photo_url: string | null;
  /** Unix seconds the client last opened their portal (migration 0127).
   *  Drives the "New since last seen" badge on portal documents. */
  client_portal_last_seen_at: number | null;
  created_at: number;
  updated_at: number;
  /** When the deal entered its CURRENT phase, from transaction_phase_history.
   *  Null on deals with no history row. Use this for days-in-phase, never
   *  updated_at — any edit resets updated_at. (2026-07-27 audit.) */
  phase_entered_at: number | null;
}

// ─── Round L-8: DA verification ────────────────────────────────────────
export interface DaComparisonRow {
  label: string;
  stored_dollars: number | null;
  da_dollars: number | null;
  matches: boolean;
  delta_dollars: number;
  matched_user_id?: number | null;
  kind?: 'office' | 'agent' | 'other';
}

export interface DaParseResponse {
  ok: true;
  document_id: number | null;
  da: {
    sale_price_dollars: number | null;
    total_commission_dollars: number | null;
    pay_to_office_dollars: number | null;
    payees: Array<{ name: string; amount_dollars: number }>;
    close_date: string | null;
    seller_names: string | null;
    buyer_names: string | null;
    da_id: string | null;
  };
  comparison: DaComparisonRow[];
  close_date_check?: { stored: string | null; da: string | null; matches: boolean };
  any_mismatches: boolean;
  cost_tenths_cents: number;
}

export function applyDa(
  txId: number,
  body: {
    sale_price_cents?: number | null;
    pay_to_office_cents?: number | null;
    close_date?: string | null;
    agent_payouts?: Array<{ user_id: number; payout_cents: number }>;
  },
): Promise<{ ok: true }> {
  return apiFetch(`/api/transactions/${txId}/da-apply`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * Build 30: where a deal's lead came from. This is the single input that
 * decides whether an agent keeps their normal rate or gives up ten points,
 * so it is a money field wearing a classification's clothes.
 *
 * 'off_market_no_team' only ever applies to flips, where it means no fee is
 * due because the team had nothing to do with that side.
 */
export type LeadOrigin = 'self' | 'team' | 'off_market_no_team';

/** A deal still waiting for someone to say where its lead came from. */
export interface PayBasisGap {
  id: number;
  type: string;
  status: string;
  close_date: number | null;
  sale_price: number | null;
  address_street: string | null;
  address_city: string | null;
  primary_agent_id: number | null;
  first_name: string | null;
  last_name: string | null;
  lead_source: string | null;
  lead_source_detail: string | null;
}

export function listPayBasisGaps(): Promise<{ deals: PayBasisGap[] }> {
  return apiFetch('/api/transactions/_meta/pay-basis-gaps');
}

export function setDealPayBasis(
  id: number,
  body: { lead_origin?: LeadOrigin | null; is_flip?: boolean },
): Promise<{ ok: true; lead_origin: LeadOrigin | null; is_flip: number }> {
  return apiFetch(`/api/transactions/${id}/pay-basis`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function bulkSetDealPayBasis(body: {
  ids: number[];
  lead_origin: LeadOrigin;
  is_flip?: boolean;
}): Promise<{ updated: number; refused: Array<{ id: number; reason: string }> }> {
  return apiFetch('/api/transactions/bulk/pay-basis', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export interface TransactionListItem extends Transaction {
  agent_first: string | null;
  agent_last: string | null;
  agent_email: string | null;
  agent_phone: string | null;
  agent_signature: string | null;
  phase_name: string | null;
}

export interface PhaseHistoryEntry {
  phase_slug: string;
  phase_name: string | null;
  entered_at: number;
  notes: string | null;
  by_first: string | null;
  by_last: string | null;
}

export interface ListFilters {
  status?: TxStatusFilter | '';
  type?: TxType | '';
  assignee?: 'me' | '' | number;
  /** Build 20: with a numeric assignee, only deals where they are the primary agent. */
  primaryOnly?: boolean;
  /** Include dead deals (cancelled/withdrawn/expired) in an unfiltered list.
   *  The Board sets this so it can render the dead columns. Default false —
   *  the day-to-day list hides dead deals unless a dead status is picked. */
  includeDead?: boolean;
  /** Build 10 (Daniel 2026-09-08): a date window. Which date it applies to
   *  (close by default), and inclusive YYYY-MM-DD bounds. */
  dateField?: 'close' | 'contract' | 'list';
  from?: string;
  to?: string;
  /** Build 10: server-side text match on street, city or zip (drill-through). */
  q?: string;
}

export function listTransactions(filters: ListFilters = {}): Promise<{ transactions: TransactionListItem[] }> {
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.type) params.set('type', filters.type);
  if (filters.assignee !== undefined && filters.assignee !== '') {
    params.set('assignee', String(filters.assignee));
  }
  if (filters.primaryOnly) params.set('primary_only', '1');
  if (filters.includeDead) params.set('include_dead', '1');
  if (filters.dateField && filters.dateField !== 'close') params.set('date_field', filters.dateField);
  if (filters.from) params.set('from', filters.from);
  if (filters.to) params.set('to', filters.to);
  if (filters.q) params.set('q', filters.q);
  const qs = params.toString();
  return apiFetch(`/api/transactions${qs ? `?${qs}` : ''}`);
}

// ─── Dead-deal states + status counts (migration 0143) ────────────────

export interface StatusCounts {
  active: number;
  under_contract: number;
  closed: number;
  cancelled: number;
  withdrawn: number;
  expired: number;
}

/** Full status-bucket ratio for the list — INCLUDES the dead buckets so they
 *  stay visible even though they're excluded from the money numbers. */
export function getStatusCounts(type?: TxType, owner?: { assignee?: 'me' | number | ''; primaryOnly?: boolean }): Promise<StatusCounts> {
  const p = new URLSearchParams();
  if (type) p.set('type', type);
  if (owner?.assignee) p.set('assignee', String(owner.assignee));
  if (owner?.primaryOnly) p.set('primary_only', '1');
  const qs = p.toString();
  return apiFetch(`/api/transactions/_meta/status-counts${qs ? `?${qs}` : ''}`);
}

/** Mark a deal dead. status → 'cancelled', listing_outcome set. Buyer deals
 *  may only pass 'cancelled'; withdrawn/expired are listing/auction-only.
 *
 *  `reason` is a key from the shared DEAD_REASONS list (migration 0186). The
 *  server REQUIRES it when the deal died under contract and rejects a value
 *  that is not on the list, so the picker builds its options from the same
 *  array rather than a copy. */
export function closeOutTransaction(
  id: number,
  outcome: ListingOutcome,
  note?: string,
  reason?: string | null,
): Promise<{
  ok: true; status: 'cancelled'; listing_outcome: ListingOutcome;
  dead_reason: string | null; died_under_contract: number;
}> {
  return apiFetch(`/api/transactions/${id}/close-out`, {
    method: 'POST',
    body: JSON.stringify({
      outcome,
      ...(note ? { note } : {}),
      ...(reason ? { reason } : {}),
    }),
  });
}

/**
 * Fall-through split for the funnel dashboard (migration 0186).
 *
 * Three buckets that must never be merged: a contract that fell through after
 * going under contract, a listing that never got there, and the deals closed
 * out before the column existed, which are unknown rather than zero.
 */
export interface FallThroughStats {
  /** died_under_contract = 1. The expensive kind. */
  fell_through: number;
  /** died_under_contract = 0. Never reached a contract. */
  never_under_contract: number;
  /** died_under_contract IS NULL. Unknown, and reported as unknown. */
  not_recorded: number;
  /** status='cancelled' with no listing_outcome at all, i.e. pre-0143 rows.
   *  Outside the report population. Here so the numbers reconcile with the
   *  status-count ratio on the transactions list, which does count them. */
  dead_without_outcome: number;
  closed: number;
  /** fell_through + closed. Echoed so the rate can be checked, not trusted. */
  rate_denominator: number;
  /** Null when the denominator is 0. A team with no history has no rate, and
   *  "0%" would read as good news. */
  fall_through_rate_pct: number | null;
  never_under_contract_by_outcome: {
    cancelled: number;
    withdrawn: number;
    expired: number;
  };
  /** Fall-throughs only, grouped by dead_reason. `reason` may be null; render
   *  it with deadReasonLabel, which says "Not recorded". */
  reasons: Array<{ reason: string | null; count: number }>;
}

/** Owner / operations / admin only, matching the funnel-stats endpoint that
 *  feeds the same page. */
export function getFallThroughStats(): Promise<FallThroughStats> {
  return apiFetch('/api/transactions/_meta/fall-through');
}

/** Undo a close-out: clears listing_outcome and reactivates the deal. When
 *  target_phase_slug is passed, the deal moves there and its status is set to
 *  match that phase (active / under_contract / closed). */
export function reopenTransaction(id: number, targetPhaseSlug?: string): Promise<{ ok: true; status: TxStatus }> {
  return apiFetch(`/api/transactions/${id}/reopen`, {
    method: 'POST',
    body: JSON.stringify(targetPhaseSlug ? { target_phase_slug: targetPhaseSlug } : {}),
  });
}

/** Clone a deal into a fresh active transaction. Returns the new deal id. */
export function duplicateTransaction(
  id: number,
  copyParties: boolean,
): Promise<{ id: number }> {
  return apiFetch(`/api/transactions/${id}/duplicate`, {
    method: 'POST',
    body: JSON.stringify({ copy_parties: copyParties }),
  });
}

/** Period filter for the production tile (Round L-4, Daniel 2026-06-09). */
export type ProductionPeriod =
  | 'this_year' | 'last_30' | 'last_90' | 'all_time'
  // Build 10 (Daniel 2026-09-08): the rest of the windows Daniel asked for.
  | 'last_60' | 'this_month' | 'last_month' | 'last_year';

export interface PortalStats {
  pipeline: { deals: number; volume_cents: number };
  /** Build 88: closed and still-scheduled are told apart. */
  closings_this_month: { deals: number; volume_cents: number; closed?: number; closed_volume_cents?: number; scheduled?: number };
  /** When a period filter is passed, this represents closed production
   *  within that window — not strictly "lifetime". The `period` field
   *  echoes which window the server actually used. */
  lifetime: { deals: number; volume_cents: number; period: string };
  /** V-1 (Daniel 2026-08-10): 'team' when these dollars cover the whole team
   *  (owner / operations / admin), 'own' when they cover only the deals the
   *  caller is on. Portal callers were always self-scoped and read 'own'. */
  scope: 'team' | 'own';
}

/** Pipeline / closings-this-month / production for the dashboards.
 *  `period` defaults to all_time server-side; the Transactions list
 *  passes 'this_year' by default per Daniel's 2026-06-09 ask. */
export function getPortalStats(period?: ProductionPeriod, owner?: { assignee?: 'me' | number | ''; primaryOnly?: boolean }): Promise<PortalStats> {
  const p = new URLSearchParams();
  if (period) p.set('period', period);
  if (owner?.assignee) p.set('assignee', String(owner.assignee));
  if (owner?.primaryOnly) p.set('primary_only', '1');
  const qs = p.toString();
  return apiFetch(`/api/transactions/_meta/portal-stats${qs ? `?${qs}` : ''}`);
}

// ─── Round L-19: Unified pipeline summary (KW Command-style board) ───
export type UnifiedPhaseKey =
  | 'cultivate'
  | 'appointment'
  | 'active'
  | 'under_contract'
  | 'closed';

export interface PipelinePhasePreviewItem {
  id: number;
  source: 'lead' | 'transaction';
  label: string;
  sub_label: string | null;
  volume_cents: number;
}

export interface PipelinePhase {
  key: UnifiedPhaseKey;
  label: string;
  source: 'lead' | 'transaction';
  count: number;
  volume_cents: number;
  avg_days: number;
  probability: number;
  preview: PipelinePhasePreviewItem[];
}

export interface PipelineSummary {
  type: TxType;
  label: string;
  phases: PipelinePhase[];
  potential_gci_cents: number;
  probable_gci_cents: number;
  potential_volume_cents: number;
  probable_volume_cents: number;
}

export interface PipelineSummaryResponse {
  pipelines: PipelineSummary[];
  commission_rate: number;
  closed_year: number;
  probabilities: Record<string, number>;
}

/** Round L-19: stacked Listings/Auctions/Buyers pipelines with combined
 *  Lead (Cultivate, Appointment) + Transaction (Active, UC, Closed) phases
 *  + per-pipeline Potential/Probable Income. */
export function getPipelineSummary(year?: number): Promise<PipelineSummaryResponse> {
  const qs = year ? `?year=${year}` : '';
  return apiFetch(`/api/transactions/_meta/pipeline-summary${qs}`);
}

/**
 * Downloads the CSV export for the current user (portal-filtered server-side).
 * Has to use raw fetch because `apiFetch` parses JSON — we want a Blob.
 */
export async function downloadTransactionsCsv(): Promise<void> {
  const { getToken } = await import('../auth/tokenStore');
  const token = getToken();
  if (!token) throw new Error('Not signed in');
  const apiBase = import.meta.env.VITE_API_BASE_URL ?? '';
  const res = await fetch(`${apiBase}/api/transactions/export.csv`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Export failed (HTTP ${res.status}). ${text.slice(0, 200)}`);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `transactions-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export type AskQuestionResult =
  | { ok: true; sent_to: string }
  | { ok: false; provider: 'unconfigured'; fallback: { to: string; to_name: string | null; subject: string; body: string } };

export interface TransactionQuestion {
  id: number;
  asked_by_portal: 'agent' | 'partner' | 'client';
  message: string;
  sent_to_email: string | null;
  send_status: 'sent' | 'queued' | 'fallback' | 'failed';
  replied_at: number | null;
  reply_note: string | null;
  created_at: number;
  asked_first: string | null;
  asked_last: string | null;
  asked_email: string | null;
  replied_first: string | null;
  replied_last: string | null;
}

export function listQuestions(txId: number): Promise<{ questions: TransactionQuestion[] }> {
  return apiFetch(`/api/transactions/${txId}/questions`);
}

// ---------------------------------------------------------------------
// Communications history — unified email + SMS feed per transaction
// ---------------------------------------------------------------------

export interface CommunicationItem {
  type: 'email' | 'sms';
  id: number;
  to: string;
  subject: string | null;
  body: string;
  status: string;
  cc: string[] | null;
  provider: string | null;
  from_user_id: number | null;
  from_name: string | null;
  sent_at: number | null;
  created_at: number;
  error: string | null;
}

export function listCommunications(txId: number): Promise<{ items: CommunicationItem[] }> {
  return apiFetch(`/api/transactions/${txId}/communications`);
}

export function markQuestionReplied(
  txId: number,
  qid: number,
  note?: string,
): Promise<{ ok: true }> {
  return apiFetch(`/api/transactions/${txId}/questions/${qid}/replied`, {
    method: 'POST',
    body: JSON.stringify({ note }),
  });
}

/**
 * Fires an "ask the agent" email from a portal user to the deal's primary
 * agent. Server returns 503 with a fallback payload when Resend isn't set up,
 * so the frontend can hand off to a mailto: link.
 */
export async function askQuestion(transactionId: number, message: string): Promise<AskQuestionResult> {
  try {
    const res = await apiFetch<{ ok: true; sent_to: string }>(
      `/api/transactions/${transactionId}/ask-question`,
      { method: 'POST', body: JSON.stringify({ message }) },
    );
    return res;
  } catch (e) {
    // 503 with provider:'unconfigured' is the documented "Resend not set up"
    // signal — convert it back to a normal return so the modal can show a
    // mailto: button instead of a failure.
    if (
      e instanceof ApiError &&
      e.status === 503 &&
      e.data &&
      typeof e.data === 'object' &&
      (e.data as { provider?: string }).provider === 'unconfigured'
    ) {
      return e.data as AskQuestionResult;
    }
    throw e;
  }
}

export interface CoAgent {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
}

export function getTransaction(
  id: number,
): Promise<{
  transaction: TransactionListItem;
  phase_history: PhaseHistoryEntry[];
  co_agents: CoAgent[];
  /** True when the deal's primary_agent has set up their personal voice
   *  profile. Used by the Marketing tab to gate Write-w/-Claude. */
  primary_agent_has_voice_profile: boolean;
  /** True when the team has a brand voice profile saved. */
  team_has_voice_profile: boolean;
}> {
  return apiFetch(`/api/transactions/${id}`);
}

export function addTransactionCoAgent(txId: number, userId: number): Promise<{ ok: true }> {
  return apiFetch(`/api/transactions/${txId}/co-agents`, {
    method: 'POST',
    body: JSON.stringify({ user_id: userId }),
  });
}

export function removeTransactionCoAgent(txId: number, userId: number): Promise<{ ok: true }> {
  return apiFetch(`/api/transactions/${txId}/co-agents/${userId}`, { method: 'DELETE' });
}

// L-101 (Daniel 2026-07-08): client portal token mgmt.
export interface ClientPortalLink {
  token: string | null;
  url: string | null;
  last_seen_at?: number | null;
}
export function getClientPortalLink(txId: number): Promise<ClientPortalLink> {
  return apiFetch(`/api/transactions/${txId}/portal-link`);
}
export function mintClientPortalLink(txId: number): Promise<ClientPortalLink> {
  return apiFetch(`/api/transactions/${txId}/portal-link`, { method: 'POST' });
}
export function revokeClientPortalLink(txId: number): Promise<{ ok: true }> {
  return apiFetch(`/api/transactions/${txId}/portal-link`, { method: 'DELETE' });
}

export interface CreateTransactionBody {
  type: TxType;
  primary_agent_id?: number;
  address_street?: string;
  address_city?: string;
  address_state?: string;
  address_zip?: string;
  list_price?: number;       // dollars
  sale_price?: number;       // dollars
  earnest_money?: number;    // dollars
  reserve_price?: number;    // dollars
  contract_date?: number | null;
  close_date?: number | null;
  list_date?: number | null;
  live_date?: number | null;
  auction_date?: number | null;
  auction_time?: string;
  auction_location?: string;
  financing_type?: string;
  mls_number?: string;
  notes?: string;
  // Migration 0146 (Daniel 2026-07-27) — buyer deals only; ignored
  // server-side for listings/auctions.
  buyer_type?: BuyerType | null;
  showing_assistant_user_id?: number | null;
  // Migration 0147 — applies to all deal types. Fraction (0.6) or whole
  // percent (60); the server normalizes. Omit/null to use the team default.
  agent_split_pct?: number | null;
  // Audit 2026-07-27 — the New form collects all of these; the create path
  // used to drop them. Server shares one allowlist with PATCH now.
  // (list_date / live_date are already declared above.)
  listing_expiration_date?: number | null;
  close_location?: string;
  auction_link_url?: string;
  kw_command_url?: string;
  authentisign_url?: string;
  em_deadline?: number | null;
  inspection_deadline?: number | null;
  appraisal_deadline?: number | null;
  financing_deadline?: number | null;
  walkthrough_date?: number | null;
}

export function createTransaction(body: CreateTransactionBody): Promise<{ ok: true; id: number }> {
  return apiFetch('/api/transactions', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// Party roles accepted by POST /api/transactions/:id/parties. Mirrors
// VALID_PARTY_ROLES on the server (apps/api/src/routes/transactions.ts).
export type PartyRole =
  | 'buyer' | 'seller' | 'co_buyer' | 'co_seller'
  | 'listing_agent' | 'buyer_agent' | 'lender' | 'title'
  | 'inspector' | 'appraiser' | 'tc' | 'other';

/** Attach a contact to a transaction as a party (buyer, seller, etc.). */
export function addTransactionParty(
  txId: number,
  body: { contact_id: number; role: PartyRole; is_primary?: boolean },
): Promise<{ ok: true; id: number }> {
  return apiFetch(`/api/transactions/${txId}/parties`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/** R-2 (Rachelle 2026-08-03): change an existing party's role in place
 *  (PATCH /api/transactions/:id/parties/:partyId). */
export function updatePartyRole(
  txId: number,
  partyId: number,
  role: PartyRole,
): Promise<{ ok: true }> {
  return apiFetch(`/api/transactions/${txId}/parties/${partyId}`, {
    method: 'PATCH',
    body: JSON.stringify({ role }),
  });
}

export type UpdateTransactionBody = Partial<
  Pick<
    Transaction,
    | 'address_street'
    | 'address_city'
    | 'address_state'
    | 'address_zip'
    | 'mls_number'
    | 'kw_command_id'
    | 'transactiondesk_id'
    | 'contract_date'
    | 'close_date'
    | 'list_date'
    | 'live_date'
    | 'close_time'
    | 'close_location'
    | 'listing_expiration_date'
    | 'auction_date'
    | 'auction_time'
    | 'auction_location'
    | 'auction_link_url'
    | 'video_url'
    | 'lockbox_type'
    | 'lockbox_code'
    | 'lockbox_location'
    | 'lockbox_notes'
    | 'sign_type'
    | 'kw_command_url'
    | 'authentisign_url'
    | 'financing_type'
    | 'em_deadline'
    | 'inspection_deadline'
    | 'appraisal_deadline'
    | 'financing_deadline'
    | 'walkthrough_date'
    | 'photo_appointment_at'
    | 'photographer_name'
    | 'photos_url'
    | 'inspection_scheduled_at'
    | 'inspector_name'
    | 'inspector_company'
    | 'appraisal_scheduled_at'
    | 'appraiser_name'
    | 'appraiser_company'
    | 'notes'
    | 'status'
    | 'primary_agent_id'
    | 'marketing_plan_json'
    | 'commission_breakdown_json'
    // Migration 0146 (Daniel 2026-07-27) — buyer deals only. Pass null to
    // clear either one back to "not set" / "no assistant".
    | 'buyer_type'
    | 'showing_assistant_user_id'
    // Migration 0147 — null resets the deal to the team default split.
    | 'agent_split_pct'
  >
> & {
  list_price?: number;       // dollars (server converts to cents)
  sale_price?: number;       // dollars
  earnest_money?: number;    // dollars
  reserve_price?: number;    // dollars
  // L-47 (Daniel 2026-06-27): property category for the auction
  // recommender. Allowed values are validated server-side.
  property_type?: 'residential' | 'commercial' | 'land' | 'multifamily' | 'other' | null;
  // L-57 (Daniel 2026-06-29): per-listing AI content targeting.
  voice_profile_kind?: 'agent' | 'team' | null;
  /** Object OR JSON string. Backend stringifies if needed. */
  target_audiences_json?: string | TargetAudiencesPayload | null;
};

/** L-57 payload shape persisted on transactions.target_audiences_json. */
export interface TargetAudiencesPayload {
  general: boolean;
  client_avatar_ids: number[];
  buyer_audience_ids: number[];
}

// L-59a (Daniel 2026-06-29): traffic + leads stats for the Marketing tab.
export interface TrafficReferringPost {
  post_id: string;
  post_title: string;
  visits: number;
}
export interface TrafficStats {
  visits_7d: number;
  visits_all_time: number;
  form_submissions_7d: number;
  form_submissions_all_time: number;
  direct_visits_7d: number;
  top_referring_posts: TrafficReferringPost[];
}
export function getTrafficStats(txId: number): Promise<TrafficStats> {
  return apiFetch(`/api/transactions/${txId}/traffic-stats`);
}

// L-58c (Daniel 2026-06-29): pre-launch readiness checklist for the
// Marketing tab. Backend computes the 6 items from a mix of transaction
// columns + marketing_plan_json + listing_photos count.
export interface PreLaunchChecklistItem {
  id: string;
  label: string;
  status: 'done' | 'incomplete';
  detail?: string | null;
}
export interface PreLaunchChecklist {
  items: PreLaunchChecklistItem[];
  incompleteCount: number;
  doneCount: number;
  total: number;
  daysToLive: number | null;
}
export function getPreLaunchChecklist(txId: number): Promise<PreLaunchChecklist> {
  return apiFetch(`/api/transactions/${txId}/pre-launch-checklist`);
}

// ─────────────────────────────────────────────────────────────────────
// Website showcase (migration 0248): the per-deal opt-in that puts a
// closed deal on the public Results page. Reads are team-wide; the save
// is leadership-only server-side (publishing a client's sale is a
// permission call, not a routine edit).
// ─────────────────────────────────────────────────────────────────────
export interface ShowcaseSettings {
  enabled: boolean;
  show_price: boolean;
  blurb: string;
}
export function getShowcase(txId: number): Promise<{ showcase: ShowcaseSettings | null }> {
  return apiFetch(`/api/transactions/${txId}/showcase`);
}
export function saveShowcase(txId: number, settings: ShowcaseSettings): Promise<{ ok: true; showcase: ShowcaseSettings }> {
  return apiFetch(`/api/transactions/${txId}/showcase`, {
    method: 'PUT',
    body: JSON.stringify(settings),
  });
}

// ─────────────────────────────────────────────────────────────────────
// D-9 (Daniel 2026-08-31): per-deal Seller's Property Disclosure waiver.
// `waived` drives the checklist; `contract_status` is the extraction/
// contract suggestion the Files tab can one-click-confirm.
// ─────────────────────────────────────────────────────────────────────
export type SpdContractStatus = 'waived' | 'within_days' | 'have' | 'required' | null;
export interface SpdState {
  waived: boolean;
  contract_status: SpdContractStatus;
}
export function getSpd(txId: number): Promise<{ spd: SpdState }> {
  return apiFetch(`/api/transactions/${txId}/spd`);
}
export function saveSpd(txId: number, waived: boolean): Promise<{ ok: true; spd: SpdState }> {
  return apiFetch(`/api/transactions/${txId}/spd`, {
    method: 'PUT',
    body: JSON.stringify({ waived }),
  });
}

// ─────────────────────────────────────────────────────────────────────
// Build 54 (Angel 2026-09-22): the earnest money receipt waiver, same shape
// as the SPD one. `waived` drops the EM receipt from the required documents
// on the Files tab, in the morning missing-docs email, and at closing.
// ─────────────────────────────────────────────────────────────────────
export interface EmWaiverState {
  waived: boolean;
}
export function getEmWaiver(txId: number): Promise<{ em: EmWaiverState }> {
  return apiFetch(`/api/transactions/${txId}/em-waiver`);
}
export function saveEmWaiver(txId: number, waived: boolean): Promise<{ ok: true; em: EmWaiverState }> {
  return apiFetch(`/api/transactions/${txId}/em-waiver`, {
    method: 'PUT',
    body: JSON.stringify({ waived }),
  });
}

// ─────────────────────────────────────────────────────────────────────
// Build 76 (Angel 2026-09-28): the MLS sheet waiver, for off-market and
// FSBO deals. `fsbo` is read-only here: an FSBO deal counts as waived
// without the box, and the Files tab says so.
// ─────────────────────────────────────────────────────────────────────
export interface MlsWaiverState {
  waived: boolean;
  fsbo: boolean;
}
export function getMlsWaiver(txId: number): Promise<{ mls: MlsWaiverState }> {
  return apiFetch(`/api/transactions/${txId}/mls-waiver`);
}
export function saveMlsWaiver(txId: number, waived: boolean): Promise<{ ok: true; mls: MlsWaiverState }> {
  return apiFetch(`/api/transactions/${txId}/mls-waiver`, {
    method: 'PUT',
    body: JSON.stringify({ waived }),
  });
}

// Build 54 (Angel 2026-09-22): FSBO. Marks a buyer deal whose seller has no
// agent, so the Tuesday drafts also write a weekly update to the seller.
export interface FsboState {
  fsbo: boolean;
  /** Set when turning FSBO on added the weekly-update task to the deal. */
  task_created?: boolean;
}
export function getFsbo(txId: number): Promise<FsboState> {
  return apiFetch(`/api/transactions/${txId}/fsbo`);
}
export function saveFsbo(txId: number, fsbo: boolean): Promise<{ ok: true } & FsboState> {
  return apiFetch(`/api/transactions/${txId}/fsbo`, {
    method: 'PUT',
    body: JSON.stringify({ fsbo }),
  });
}

// Deal setup (Daniel 2026-11-04): the answers that decide which documents a
// deal needs. GET also carries TR's suggestion for a person to confirm.
export type OtherSideAnswer = 'agent' | 'none' | 'team';
export interface DealSetupState {
  other_side: OtherSideAnswer | null;
  year_built: number | null;
  well_septic: boolean | null;
  estate_trust: boolean | null;
  confirmed_at: number | null;
  confirmed_by: string | null;
}
export interface DealSetupSuggestion {
  other_side: OtherSideAnswer | null;
  year_built: number | null;
  estate_trust: boolean | null;
  reasons: string[];
}
export function getDealSetup(txId: number): Promise<{ setup: DealSetupState; suggestion: DealSetupSuggestion }> {
  return apiFetch(`/api/transactions/${txId}/setup`);
}
export function saveDealSetup(txId: number, body: {
  other_side: OtherSideAnswer | null;
  year_built: number | null;
  well_septic: boolean | null;
  estate_trust: boolean | null;
}): Promise<{ ok: true; task_created: boolean }> {
  return apiFetch(`/api/transactions/${txId}/setup`, { method: 'PUT', body: JSON.stringify(body) });
}

export function updateTransaction(id: number, patch: UpdateTransactionBody): Promise<{ ok: true }> {
  return apiFetch(`/api/transactions/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

// ─────────────────────────────────────────────────────────────────────
// M-17 (Daniel 2026-07-31): close-out gate. Marking a deal closed is
// gated server-side on the required document-checklist slots + the
// commission being set. A blocked close returns 409 with this shape.
// ─────────────────────────────────────────────────────────────────────
export interface CloseBlocker {
  code: 'documents' | 'commission' | 'clients' | 'signer';
  detail: string;
  /** documents only: every empty required checklist slot, phase-labelled. */
  missing?: Array<{ phase: string; label: string; doc_type: string }>;
  /** clients only (Build 68): buyers or sellers on the deal with no tier. */
  clients?: Array<{ id: number; name: string }>;
  /** signer only (Build 74): companies on the deal with no person behind them. */
  entities?: Array<{ id: number; name: string }>;
}
export interface CloseGateData {
  error: string;
  blockers: CloseBlocker[];
  /** True when the caller's roles (owner/operations) may force the close. */
  can_force: boolean;
}

/** Narrow an ApiError to the structured 409 close-gate body, or null. */
export function closeGateFromError(e: unknown): CloseGateData | null {
  if (!(e instanceof ApiError) || e.status !== 409) return null;
  const d = e.data;
  if (d && typeof d === 'object' && Array.isArray((d as { blockers?: unknown }).blockers)) {
    return d as unknown as CloseGateData;
  }
  return null;
}

/** Mark a deal closed. `force` retries past the gate (owner/operations). */
export function markTransactionClosed(id: number, force = false): Promise<{ ok: true }> {
  return apiFetch(`/api/transactions/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(force ? { status: 'closed', force: true } : { status: 'closed' }),
  });
}

export function deleteTransaction(id: number): Promise<{ ok: true }> {
  return apiFetch(`/api/transactions/${id}`, { method: 'DELETE' });
}

/** Build 62: the deal Trash. */
export interface TrashedTransaction {
  id: number;
  type: 'auction' | 'listing' | 'buyer';
  status: string;
  address_street: string | null;
  address_city: string | null;
  address_state: string | null;
  deleted_at: number;
  deleted_by_user_id: number | null;
  deleted_by_name: string | null;
  agent_name: string | null;
  task_count: number;
}
export function listTrashedTransactions(): Promise<{ transactions: TrashedTransaction[]; days: number; can_restore_all: boolean }> {
  return apiFetch('/api/transactions/_meta/trash');
}
export function restoreTransaction(id: number): Promise<{ ok: true; id: number; tasks_restored: number }> {
  return apiFetch(`/api/transactions/${id}/restore`, { method: 'POST' });
}

/** L-61a-followup: sweep orphaned shell transactions from the L-61a
 *  wizard. Called on wizard mount as a best-effort cleanup pass. */
export function cleanupOrphanShells(): Promise<{ ok: true; swept: number }> {
  return apiFetch(`/api/transactions/cleanup-orphan-shells`, { method: 'POST' });
}

/**
 * Task #28 (Daniel 2026-07-01): fetch server-resolved merge values
 * for a transaction. Same authoritative map the packet-send endpoint
 * uses, so client-side email flows can resolve every field (including
 * title_company_name, title_payment_link, client_intake_url) instead
 * of the frontend's incomplete `resolveMergeFields()` shim.
 */
export function getMergeValues(
  txId: number,
  purpose?: 'email',
): Promise<{ values: Record<string, string> }> {
  // Build 99: 'email' hides an auction's list and reserve prices.
  return apiFetch(`/api/transactions/${txId}/merge-values${purpose ? `?for=${purpose}` : ''}`);
}

// ─────────────────────────────────────────────────────────────────────
// L-62c (Daniel 2026-07-01): Under Contract packet — email + Timeline
// PDF bundled into a single send action.
// ─────────────────────────────────────────────────────────────────────
export interface UnderContractPacketPreview {
  preview: true;
  to: string[];
  subject: string;
  body: string;
  attachment: { filename: string; size_bytes: number; mime: string };
}
export interface UnderContractPacketSent {
  ok: true;
  message_id: string;
  sent_to: string[];
  attachment_filename: string;
}

/** Preview mode: resolves subject/body/recipients + returns attachment
 *  metadata but doesn't fire the email. Used to populate the preview
 *  modal before the real send. */
export function previewUnderContractPacket(
  txId: number,
): Promise<UnderContractPacketPreview> {
  return apiFetch(`/api/transactions/${txId}/send-under-contract-packet`, {
    method: 'POST',
    body: JSON.stringify({ preview: true }),
  });
}

/** Real send: fires the email with the timeline PDF attached and
 *  logs the send to email_sends + transaction_messages. Overrides
 *  are optional — omit to send exactly what the preview showed. */
export function sendUnderContractPacket(
  txId: number,
  overrides?: { to?: string[]; subject?: string; body?: string },
): Promise<UnderContractPacketSent> {
  return apiFetch(`/api/transactions/${txId}/send-under-contract-packet`, {
    method: 'POST',
    body: JSON.stringify(overrides ?? {}),
  });
}

/** K-2 Kanban: set the transaction's current phase by slug. Powers
 *  drag-and-drop on the board view. */
export function setTransactionPhase(
  id: number,
  targetPhaseSlug: string,
  notes?: string,
): Promise<{ ok: true; phase_slug: string; tasks_generated: number; no_op?: boolean }> {
  return apiFetch(`/api/transactions/${id}/set-phase`, {
    method: 'POST',
    body: JSON.stringify({
      target_phase_slug: targetPhaseSlug,
      ...(notes ? { notes } : {}),
    }),
  });
}

/**
 * Fire a .ics calendar invite for a listing milestone. Recipients default
 * to the team's active members (owner/operations/admin/agent/marketing)
 * plus the deal's primary agent. Use `extraEmails` to loop in showing
 * partners, the seller, etc.
 */
export interface SendCalendarInviteBody {
  kind: 'listing_live' | 'open_house';
  /** Unix seconds — the event date (UTC midnight is fine for all-day). */
  date: number;
  /** Free-text time window — surfaces in the invite body, not the ics
   *  time slot itself unless `allDay` is false. */
  time?: string;
  /** Free-text notes shown in the invite body (e.g. "Co-hosted with Sophia"). */
  notes?: string;
  /** False → treat as a 2-hour timed event. Defaults to true (all-day). */
  allDay?: boolean;
  extraEmails?: string[];
}
export function sendCalendarInvite(
  txId: number,
  body: SendCalendarInviteBody,
): Promise<{ ok: true; recipients: string[] }> {
  return apiFetch(`/api/transactions/${txId}/send-calendar-invite`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function publishListing(id: number): Promise<{ ok: true; share_token: string }> {
  return apiFetch(`/api/transactions/${id}/share`, { method: 'POST' });
}

export function unpublishListing(id: number): Promise<{ ok: true }> {
  return apiFetch(`/api/transactions/${id}/share`, { method: 'DELETE' });
}

export function publicListingUrl(token: string): string {
  // L-59b-patch (Daniel 2026-06-29): use the API base URL, not the app
  // origin. The public listing HTML is served by the API worker
  // (transaction-rocket-api.turnkey.workers.dev), not the app SPA
  // (app.transactionrocket.com). Using window.location.origin was
  // routing shares back into the SPA where /public/l/* doesn't exist
  // and the router fell through to the Marketing tab default.
  const base = import.meta.env.VITE_API_BASE_URL
    || (typeof window !== 'undefined' ? window.location.origin : '');
  return `${base}/public/listings/${token}`;
}

/**
 * L-59a (Daniel 2026-06-29): build a slug-based share URL, optionally
 * with a ?p={postId} suffix that attributes downstream visits back to
 * a specific marketing post. When agents copy a link from a post row,
 * they use this helper — every visit that comes in via that URL gets
 * logged with the post id and shows up in the "Top referring posts"
 * list on the traffic panel.
 *
 * L-59b-patch (Daniel 2026-06-29): resolve against the API base URL,
 * NOT window.location.origin. See publicListingUrl above — the public
 * listing page is served from the API worker, not the SPA host.
 */
export function publicListingUrlBySlug(slug: string, referringPostId?: string | null): string {
  const base = import.meta.env.VITE_API_BASE_URL
    || (typeof window !== 'undefined' ? window.location.origin : '');
  const url = `${base}/public/l/${slug}`;
  if (referringPostId) {
    return `${url}?p=${encodeURIComponent(referringPostId)}`;
  }
  return url;
}

// ---------------------------------------------------------------------
// Agents picker (for assigning primary_agent_id in forms)
// ---------------------------------------------------------------------

export interface AgentPick {
  id: number;
  first_name: string;
  last_name: string;
}

export function listAgents(): Promise<{ agents: AgentPick[] }> {
  return apiFetch('/api/team/agents');
}

// L-69 (Daniel 2026-07-04): CC-candidate list for SendEmailModal's
// Cc picker. Any team member with an email + agent-portal membership.
// Response includes the caller's own user id so the modal can flag
// "+ Me" separately from the general team list.
export interface CcCandidate {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  role: string | null;
}
export function listCcCandidates(): Promise<{
  candidates: CcCandidate[];
  /** R-1 (Rachelle 2026-08-03): team-level extra Cc addresses (not TR
   *  users — e.g. daniel@bidwichita.com). Editable on the Team page. */
  extras?: string[];
  caller_user_id: number | null;
}> {
  return apiFetch('/api/team/cc-candidates');
}

// L-70 (Daniel 2026-07-04): auto-Cc / side-inbox preview + settings.
export interface EmailDefaultsResponse {
  standard_cc: Array<{ id: number; first_name: string; last_name: string; email: string }>;
  deal_agent: { id: number; first_name: string; last_name: string; email: string } | null;
  /** T-1 (Daniel 2026-08-12): the team's transaction coordinator
   *  (teams.tc_user_id). Only ever populated for a transaction email,
   *  and null when no TC seat is set. */
  transaction_coordinator: {
    id: number; first_name: string | null; last_name: string | null; email: string;
  } | null;
  self_bcc: string | null;
}
export function getEmailDefaults(transactionId?: number): Promise<EmailDefaultsResponse> {
  const suffix = transactionId ? `?transaction_id=${transactionId}` : '';
  return apiFetch(`/api/team/email-defaults${suffix}`);
}
export interface MyEmailPrefs {
  cc_self_email: string | null;
  always_cc_on_team: boolean;
}
export function getMyEmailPrefs(): Promise<MyEmailPrefs> {
  return apiFetch('/api/team/my-email-prefs');
}
export function updateMyEmailPrefs(body: { cc_self_email?: string | null; always_cc_on_team?: boolean }): Promise<{ ok: true }> {
  return apiFetch('/api/team/my-email-prefs', {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

// L-76 (Daniel 2026-07-04): sphere growth metric per agent for the
// Home dashboard.
export interface SphereGrowthAgent {
  user_id: number;
  first_name: string;
  last_name: string;
  /** Contacts moved into a sphere stage in the window. */
  count: number;
  /** Build 96: Mets in this agent's database today. */
  mets: number;
  /** Build 96: everyone in this agent's database (vendors left out). */
  contacts: number;
}
export interface SphereGrowthResponse {
  window: string;
  agents: SphereGrowthAgent[];
  total: number;
  total_mets: number;
  total_contacts: number;
}
export function getSphereGrowth(window: 'this_month' | 'last_month' | 'ytd' = 'this_month'): Promise<SphereGrowthResponse> {
  return apiFetch(`/api/team/sphere-growth?window=${window}`);
}

// ---------------------------------------------------------------------
// Display helpers
// ---------------------------------------------------------------------

export function typeLabel(t: TxType): string {
  return { auction: 'Auction', listing: 'Listing', buyer: 'Buyer' }[t];
}

export function statusLabel(s: TxStatus): string {
  return {
    active: 'Active',
    under_contract: 'Under contract',
    closed: 'Closed',
    cancelled: 'Cancelled',
  }[s];
}

export function statusColor(s: TxStatus): { bg: string; fg: string } {
  switch (s) {
    case 'active':
      return { bg: 'var(--brand-primary-soft)', fg: 'var(--text-heading)' };
    case 'under_contract':
      return { bg: 'var(--warning-soft)', fg: 'var(--warning)' };
    case 'closed':
      return { bg: 'var(--success-soft)', fg: 'var(--success)' };
    case 'cancelled':
      return { bg: 'var(--bg-surface-2)', fg: 'var(--text-muted)' };
  }
}

/** Human label for a dead-deal outcome (migration 0143). */
export function listingOutcomeLabel(o: ListingOutcome): string {
  return { cancelled: 'Cancelled', withdrawn: 'Withdrawn', expired: 'Expired' }[o];
}

/** One-line definition shown as helper text next to the "Mark as…" control.
 *
 *  Two of these carried em dashes, which house style does not allow and which
 *  read badly at 13px on a phone. Rewritten as two short sentences each, and
 *  worded to match outcomeMeaning() in shared-types so the sheet and the
 *  close-out email say the same thing. */
export function listingOutcomeHelp(o: ListingOutcome): string {
  return {
    cancelled: 'The agreement has ended. There is no agreement in place.',
    withdrawn: 'Off the market, but the agreement is still in place.',
    expired: 'The listing agreement expired. There is no agreement in place.',
  }[o];
}

/** Effective status label — shows the dead-deal outcome when set, else the
 *  underlying status. Dead deals all carry status='cancelled'. */
export function effectiveStatusLabel(t: { status: TxStatus; listing_outcome: ListingOutcome | null }): string {
  return t.listing_outcome ? listingOutcomeLabel(t.listing_outcome) : statusLabel(t.status);
}

export function formatAddress(t: Transaction): string {
  const parts = [t.address_street, t.address_city, t.address_state, t.address_zip].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : '(no address)';
}

// ---------------------------------------------------------------------
// Marketing post + MLS caption — per-post Claude generation, 5-state
// status workflow, and the property-level MLS description.
// ---------------------------------------------------------------------

export type MarketingPostStatusApi = 'draft' | 'submitted' | 'approved' | 'scheduled' | 'posted';

/** Generates a fresh caption for one post in the transaction's marketing
 *  plan, saves it server-side, and returns the new caption. */
export function generateMarketingPost(
  txId: number,
  postId: string,
  extraContext?: string,
): Promise<{ caption: string; cost_tenths_cents: number }> {
  return apiFetch(`/api/transactions/${txId}/marketing-posts/${postId}/generate`, {
    method: 'POST',
    body: JSON.stringify({ extra_context: extraContext ?? null }),
  });
}

/** Batch-generate Claude captions for every (draft) marketing post in
 *  one round-trip. Replaces the old "Batch content prompt" copy-paste
 *  flow with a real Claude call that fills in every caption + saves
 *  them server-side. Costs ~$0.08-0.15 per listing (one large call vs
 *  N small calls). Per-post Prompt buttons still work for one-offs. */
export function batchGenerateMarketingPosts(
  txId: number,
  options?: { only_drafts?: boolean },
): Promise<{ ok: true; updated: number; targeted: number; skipped: number; cost_tenths_cents: number }> {
  return apiFetch(`/api/transactions/${txId}/marketing-posts/batch-generate`, {
    method: 'POST',
    body: JSON.stringify({ only_drafts: options?.only_drafts ?? true }),
  });
}

/** Wipes the marketing plan's posts and re-seeds with the current default
 *  template (30-day for listings/buyers, auction-template for auctions).
 *  Preserves listing_live_date, open houses, property details, budget. */
export function resetMarketingPlanPosts(
  txId: number,
): Promise<{ ok: true; post_count: number }> {
  return apiFetch(`/api/transactions/${txId}/marketing-posts/reset`, {
    method: 'POST',
    body: JSON.stringify({ confirm: true }),
  });
}

/** Moves a marketing post through the 5-state workflow. The server
 *  enforces approval gating — only owner/operations can approve
 *  auctions; the deal's primary agent (or ops/owner/admin) can
 *  approve listings/buyer deals.
 *
 *  `note` is honored only when sending a post BACK to draft from a
 *  forward state — it becomes the rejection_note on the post and
 *  appears to the agent next to the description until they regenerate. */
export function transitionMarketingPost(
  txId: number,
  postId: string,
  to: MarketingPostStatusApi,
  options?: { posted_at?: number; note?: string },
): Promise<{
  ok: true;
  status: MarketingPostStatusApi;
  posted_at: number | null;
  /** Set when approving: what happened when TR sent the post to Metricool. */
  metricool?:
    | { status: 'sent'; scheduled: number; drafted: number; failed: number; alreadySent: number }
    | { status: 'not_connected' }
    | { status: 'no_brand' }
    | { status: 'error'; message: string }
    | null;
}> {
  return apiFetch(`/api/transactions/${txId}/marketing-posts/${postId}/transition`, {
    method: 'POST',
    body: JSON.stringify({
      to,
      ...(options?.posted_at ? { posted_at: options.posted_at } : {}),
      ...(options?.note ? { note: options.note } : {}),
    }),
  });
}

/** Bulk transition — move N posts at once. The server filters posts
 *  currently in `from` and moves them to `to` (or only the post_ids
 *  you pass, if you want a fine-grained subset). Returns moved count
 *  and any skipped posts with a reason. Same approval gating as the
 *  single-post transition. */
export function bulkTransitionMarketingPosts(
  txId: number,
  args: { from: MarketingPostStatusApi; to: MarketingPostStatusApi; post_ids?: string[] },
): Promise<{ ok: true; moved: number; skipped: Array<{ post_id: string; reason: string }>; metricool_queued?: boolean }> {
  return apiFetch(`/api/transactions/${txId}/marketing-posts/bulk-transition`, {
    method: 'POST',
    body: JSON.stringify(args),
  });
}

// ─── M-21 (Daniel 2026-08-12): merge-based plan saves ─────────────────
// The editor used to PUT the ENTIRE marketing plan blob every 1.5s via
// updateTransaction, so two people editing the same plan lost whoever
// saved first. It now sends only what changed since its last confirmed
// save, and the server merges that into the stored plan under a version
// counter. A save whose base_version is stale is refused with 409 and
// code 'plan_version_conflict'; the response body carries the current
// plan + version so the editor can reload, reapply, and tell the person.
//
// These endpoints live under /api/metricool/ for now; see the M-21
// comment in apps/api/src/routes/metricool.ts for why.

/** One post's changed fields. Only the named fields are written; the
 *  server keeps everything else on the post exactly as stored. */
export interface MarketingPostPatch {
  id: string;
  fields: Record<string, unknown>;
}

export interface MarketingPlanChangePayload {
  fields?: Record<string, unknown>;
  posts_add?: Array<Record<string, unknown> & { id: string }>;
  posts_patch?: MarketingPostPatch[];
  posts_delete?: string[];
}

export interface MarketingPlanSnapshot {
  plan_json: string;
  version: number;
}

/** Shape of the 409 body when a save is refused as stale. */
export interface MarketingPlanConflictBody {
  code: 'plan_version_conflict';
  version: number;
  plan_json: string;
}

/** Narrow an ApiError's data to the conflict body, or null. */
export function readPlanConflict(err: unknown): MarketingPlanConflictBody | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null;
  const d = err.data as Partial<MarketingPlanConflictBody> | null;
  if (!d || d.code !== 'plan_version_conflict') return null;
  if (typeof d.version !== 'number' || typeof d.plan_json !== 'string') return null;
  return { code: d.code, version: d.version, plan_json: d.plan_json };
}

export function getMarketingPlanSnapshot(txId: number): Promise<MarketingPlanSnapshot> {
  return apiFetch(`/api/metricool/marketing-plan/${txId}`);
}

export function patchMarketingPlan(
  txId: number,
  baseVersion: number,
  change: MarketingPlanChangePayload,
): Promise<{ ok: true; version: number; plan_json: string }> {
  return apiFetch(`/api/metricool/marketing-plan/${txId}`, {
    method: 'PATCH',
    body: JSON.stringify({ base_version: baseVersion, ...change }),
  });
}

/** Generates the MLS listing caption from the property details + voice
 *  profiles. Listings + auctions only; buyer deals are rejected
 *  server-side. */
export function generateMlsCaption(
  txId: number,
): Promise<{ caption: string; cost_tenths_cents: number }> {
  return apiFetch(`/api/transactions/${txId}/mls-caption`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

// ─── Listing photos + public slug ────────────────────────────────────

export interface ListingPhoto {
  id: number;
  transaction_id: number;
  public_url: string;
  alt_text: string | null;
  position: number;
}

export function listListingPhotos(txId: number): Promise<{ photos: ListingPhoto[] }> {
  return apiFetch(`/api/transactions/${txId}/photos`);
}

/** Multipart upload to R2. Accepts multiple files in one call. */
export async function uploadListingPhotos(
  txId: number,
  files: File[],
): Promise<{ ok: true; uploaded: Array<{ id: number; public_url: string; position: number }> }> {
  const { getToken } = await import('../auth/tokenStore');
  const token = getToken();
  if (!token) throw new Error('Not signed in');
  const apiBase = import.meta.env.VITE_API_BASE_URL ?? '';
  const fd = new FormData();
  for (const f of files) fd.append('file', f);
  const res = await fetch(`${apiBase}/api/transactions/${txId}/photos`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: fd,
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({})) as { error?: string };
    throw new Error(data.error ?? `HTTP ${res.status}`);
  }
  return res.json();
}

export function updateListingPhoto(
  txId: number,
  photoId: number,
  patch: { alt_text?: string | null; position?: number },
): Promise<{ ok: true }> {
  return apiFetch(`/api/transactions/${txId}/photos/${photoId}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

export function deleteListingPhoto(txId: number, photoId: number): Promise<{ ok: true }> {
  return apiFetch(`/api/transactions/${txId}/photos/${photoId}`, { method: 'DELETE' });
}

/** Sets or clears the public URL slug for /public/l/:slug. Empty/null clears. */
export function setListingPublicSlug(
  txId: number,
  slug: string | null,
): Promise<{ ok: true; public_slug: string | null }> {
  return apiFetch(`/api/transactions/${txId}/public-slug`, {
    method: 'PATCH',
    body: JSON.stringify({ slug }),
  });
}

/** Saves an edited MLS caption (after generation or hand-written). Pass
 *  an empty string to clear. */
export function saveMlsCaption(txId: number, caption: string): Promise<{ ok: true; caption: string }> {
  return apiFetch(`/api/transactions/${txId}/mls-caption`, {
    method: 'PATCH',
    body: JSON.stringify({ caption }),
  });
}

// ---------------------------------------------------------------------
// Showing stats for the client portal (Daniel 2026-07-05).
// ---------------------------------------------------------------------
export interface ShowingStats {
  /** Latest week's showings; null when no fresh snapshot (≤10 days old). */
  this_week: number | null;
  all_time: number;
  weeks_tracked: number;
  latest_snapshot_at: number | null;
}

export function getShowingStats(txId: number): Promise<ShowingStats> {
  return apiFetch(`/api/transactions/${txId}/showing-stats`);
}

// ---------------------------------------------------------------------
// C-3 (Daniel 2026-08-10): the Keller Williams tail of the commission
// waterfall, for the producing agent on this deal.
//
// Every field is INTEGER CENTS. C-1's commission.ts works in dollars as
// floats and stays that way (see the money note at the top of that file);
// C-3 is cents end to end, and this is the wire format, so the card divides
// by 100 exactly once at render time.
// ---------------------------------------------------------------------
export interface KwSplitPreview {
  agent_user_id: number | null;
  /** The producing agent's own GCI at KW: their side of the C-1 waterfall,
   *  after outside referrals and before inside ones. */
  agent_side_cents: number;
  /** Showing assistant fee plus any team-side party, from C-1. Not a KW
   *  charge, but the agent's true net has to clear it too. */
  in_house_deductions_cents: number;
  company_dollar_cents: number;
  company_dollar_pct: number;
  royalty_cents: number;
  royalty_pct: number;
  per_transaction_fee_cents: number;
  /** KW Cares + KW Next Gen + E&O, summed. */
  other_deductions_cents: number;
  agent_true_net_cents: number;
  company_dollar_capped: boolean;
  royalty_capped: boolean;
  company_dollar_remaining_cents: number;
  royalty_remaining_cents: number;
  company_dollar_period_end: number;
  royalty_period_end: number;
  /** False when nobody has set this agent's KW settings up. Everything reads
   *  zero and the card says so, rather than implying they owe nothing. */
  has_settings: boolean;
  /** True once the deal closed and the charges were RECORDED on it. The card
   *  says "cost" rather than "would cost", because they are different claims
   *  and a closed deal must show what really happened. */
  is_recorded: boolean;
}

/** `kw` is null when the viewer may not see this agent's money, or when the
 *  deal has no primary agent to charge. `kw_visible` tells the two apart. */
export function getKwSplits(txId: number): Promise<{
  kw: KwSplitPreview | null;
  kw_visible: boolean;
}> {
  return apiFetch(`/api/transactions/${txId}/kw-splits`);
}

// ─── Build 13 (Daniel 2026-09-15): change a deal's type, listing <-> auction ──
export type ConvertibleType = 'listing' | 'auction';

export interface TypeChangePreview {
  ok: boolean;
  error?: string;
  from: string;
  to: ConvertibleType;
  current_phase: { slug: string; name: string; implication: string | null };
  target_phase: { slug: string; name: string } | null;
  open_tasks_to_cancel: number;
  templates_to_generate: number;
  notes: string[];
}

export interface TypeChangeResult {
  ok: true;
  from: string;
  to: ConvertibleType;
  phase_slug: string;
  phase_name: string;
  status: string;
  tasks_cancelled: number;
  tasks_generated: number;
}

export function previewTypeChange(id: number, to: ConvertibleType): Promise<TypeChangePreview> {
  return apiFetch(`/api/transactions/${id}/type-change/preview?to=${to}`);
}

export function changeTransactionType(id: number, to: ConvertibleType, note?: string): Promise<TypeChangeResult> {
  return apiFetch(`/api/transactions/${id}/type-change`, {
    method: 'POST',
    body: JSON.stringify({ to, note: note ?? null }),
  });
}

// ─── Build 14 (Daniel 2026-09-15): one auction, many lots ──────────────────
export interface LotRow {
  id: number;
  lot_number: number;
  lot_label: string | null;
  address_street: string | null;
  address_city: string | null;
  address_state: string | null;
  address_zip: string | null;
  list_price: number | null;
  sale_price: number | null;
  status: string;
  current_phase_slug: string;
  phase_name: string | null;
  contract_date: number | null;
  close_date: number | null;
  primary_agent_id: number | null;
  public_slug: string | null;
  /** Same value on every lot sold on one contract together. */
  sold_with_group: number | null;
}

export interface LotContext {
  parent: { id: number; address: string; lot_count: number } | null;
  is_parent: boolean;
  is_lot: boolean;
  me: { lot_number: number; lot_label: string | null } | null;
  lots: LotRow[];
}

export function getLots(id: number): Promise<LotContext> {
  return apiFetch(`/api/transactions/${id}/lots`);
}

export interface CreateLotBody {
  address_street: string;
  address_city?: string | null;
  address_state?: string | null;
  address_zip?: string | null;
  /** Cents. */
  list_price?: number | null;
  lot_label?: string | null;
  copy_parties?: boolean;
}

export function createLot(parentId: number, body: CreateLotBody): Promise<{ ok: true; lot_id: number; lot_number: number }> {
  return apiFetch(`/api/transactions/${parentId}/lots`, { method: 'POST', body: JSON.stringify(body) });
}

export function attachLots(
  parentId: number,
  transactionIds: number[],
): Promise<{ ok: true; attached: number; skipped: Array<{ id: number; reason: string }> }> {
  return apiFetch(`/api/transactions/${parentId}/lots/attach`, {
    method: 'POST',
    body: JSON.stringify({ transaction_ids: transactionIds }),
  });
}

export interface SellTogetherBody {
  lot_ids: number[];
  contract_date: number;
  close_date: number | null;
  /** Cents. */
  total_sale_price: number;
  /** Cents per lot id. Null splits evenly. */
  allocation: Record<number, number> | null;
  buyer: { first_name: string; last_name: string | null; email: string | null; phone: string | null } | null;
  note: string | null;
}

export function sellLotsTogether(
  parentId: number,
  body: SellTogetherBody,
): Promise<{ ok: true; group: number; lots: Array<{ id: number; sale_price: number }>; tasks_generated: number }> {
  return apiFetch(`/api/transactions/${parentId}/lots/sell-together`, { method: 'POST', body: JSON.stringify(body) });
}

export function detachLot(parentId: number, lotId: number): Promise<{ ok: true }> {
  return apiFetch(`/api/transactions/${parentId}/lots/${lotId}/detach`, { method: 'POST' });
}


// ─── KW fees from the Agent Commission Details PDF ──────────────────────
export interface KwFeesParsed {
  agent_name: string | null;
  total_commission_dollars: number | null;
  eo_dollars: number | null;
  royalty_dollars: number | null;
  company_dollar_dollars: number | null;
  total_brokerage_fees_dollars: number | null;
  agent_check_dollars: number | null;
  close_date: string | null;
}
export interface KwFeesRow {
  agent_name: string;
  user_id: number | null;
  total_commission_cents: number | null;
  eo_cents: number;
  royalty_cents: number;
  company_dollar_cents: number;
  total_fees_cents: number;
  agent_check_cents: number | null;
  updated_at: number;
}

export function getKwFees(txId: number): Promise<{ fees: KwFeesRow[] }> {
  return apiFetch(`/api/transactions/${txId}/kw-fees`);
}

export function saveKwFees(
  txId: number,
  body: {
    agent_name: string;
    total_commission_cents: number | null;
    eo_cents: number;
    royalty_cents: number;
    company_dollar_cents: number;
    total_fees_cents: number;
    agent_check_cents: number | null;
  },
): Promise<{ ok: true }> {
  return apiFetch(`/api/transactions/${txId}/kw-fees`, { method: 'POST', body: JSON.stringify(body) });
}

/** Daniel 2026-10-29: the listing's page on Zillow, Realtor.com and Homes.com. */
export interface PortalLinks {
  zillow_url: string | null;
  realtor_url: string | null;
  homes_url: string | null;
}
export function getPortalLinks(transactionId: number): Promise<PortalLinks> {
  return apiFetch(`/api/transactions/${transactionId}/portal-links`);
}
export function savePortalLinks(transactionId: number, patch: Partial<PortalLinks>): Promise<PortalLinks & { ok: true }> {
  return apiFetch(`/api/transactions/${transactionId}/portal-links`, {
    method: 'PUT',
    body: JSON.stringify(patch),
  });
}

/** Daniel 2026-10-29: the 2 to 5 homes competing with a listing. */
export interface ListingCompetitor {
  id: number;
  status: 'proposed' | 'confirmed';
  listing_key: string;
  mls_number: string;
  address: string;
  subdivision: string | null;
  status_label: string;
  list_price: number | null;
  price_at_add: number | null;
  price_change: number | null;
  price_cuts: number;
  beds: number | null;
  baths: number | null;
  sqft: number | null;
  days_on_market: number | null;
  pending_date: string | null;
  sold_date: string | null;
  miles: number | null;
}
export interface CompetitorSuggestion {
  listing_key: string;
  mls_number: string;
  address: string;
  subdivision: string | null;
  list_price: number | null;
  beds: number | null;
  baths: number | null;
  sqft: number | null;
  days_on_market: number | null;
  miles: number | null;
  same_subdivision: boolean;
}
export interface CompetitorsResponse {
  competitors: ListingCompetitor[];
  suggestions: CompetitorSuggestion[];
  can_confirm: boolean;
  has_subject: boolean;
  subject_note: string | null;
  min: number;
  max: number;
}
export function getCompetitors(txId: number): Promise<CompetitorsResponse> {
  return apiFetch(`/api/transactions/${txId}/competitors`);
}
export function addCompetitor(txId: number, body: { listing_key?: string; mls_number?: string }): Promise<{ ok: true; status: string }> {
  return apiFetch(`/api/transactions/${txId}/competitors`, { method: 'POST', body: JSON.stringify(body) });
}
export function confirmCompetitor(txId: number, cid: number): Promise<{ ok: true }> {
  return apiFetch(`/api/transactions/${txId}/competitors/${cid}/confirm`, { method: 'POST' });
}
export function removeCompetitor(txId: number, cid: number): Promise<{ ok: true }> {
  return apiFetch(`/api/transactions/${txId}/competitors/${cid}`, { method: 'DELETE' });
}
